/**
 * ProfileLogsTable — the Logs tab shared by the Resident, Area, Device, User and
 * Server profiles.
 *
 * Replaces four near-identical components (resident 229 lines, area 228,
 * device 262, user 150) that differed only in the log-type field name, the
 * heading, and two device-only touches. Adding per-column filters to four
 * copies would have quadrupled the wiring, so they collapse here instead.
 *
 * Named for profiles rather than ADT because user logs are not admit/discharge
 * events -- they are logins, role changes and password resets.
 *
 * Filtering follows the ResidentsPage pattern: a FilterPopover per column and
 * an ActiveFilterBar above the table, replacing the single log-type <select>.
 * That matters most on Devices, where maintenance telemetry (log types 1–14,
 * written by the edge boxes) far outnumbers the ADT rows.
 *
 * Detail comes from `log_info`; see LogDetailCell and docs/ADT_LOGGING_PLAN.md.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoUTCToLocal } from '../../libs.jsx';
import usePersistedPerPage from '../../hooks/usePersistedPerPage.js';
import FilterPopover from './FilterPopover.jsx';
import ActiveFilterBar from './ActiveFilterBar.jsx';
import Pagination from './Pagination.jsx';
import LogDetailCell, { logInfoText } from './LogDetailCell.jsx';
import { ariaSort } from './SortableTh.jsx';
/* Its own stylesheets (audit C12). index.jsx already loads these first, so this does not
   move them in the cascade; it states the dependency, so the component stays styled
   wherever it is reused. */
import '../../styles/dp-primitives.css';
import '../../styles/list-primitives.css';
import '../../styles/pp-primitives.css';

const COLUMN_LABELS = {
  create_time: 'Timestamp',
  log_type: 'Log Event',
  detail: 'Details',
  display_name: 'Performed by',
};

const ProfileLogsTable = ({
  logData,
  tzOffset,
  /* Row field holding the log type name: resident_log_type_name etc. */
  typeKey,
  title,
  /* Devices link the actor to their profile; the other two tabs do not. */
  linkUsers = false,
  /* Row field to show when a row has no structured log_info. Devices pass
     'source', which is where suppression windows live until that call site is
     migrated — dropping it would lose information already on screen. */
  detailFallbackKey = null,
  /* Internal users see rows flagged is_customer_visible = 0; customers do not. */
  showInternal = false,
  /* Server logs have no actor (modified_by is always the system), so the Server
     profile hides the column rather than show a column of dashes. */
  showActor = true,
  emptyTitle = 'No logs',
  emptyText = 'No log entries are available.',
}) => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortCol, setSortCol] = useState('create_time');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  /* Derive the display fields once, so filtering, searching, sorting and
     rendering all agree on what a row's Details cell actually says. */
  const rows = useMemo(() => (logData || []).map((r) => {
    const type = r[typeKey] || '';
    /* A fallback that just restates the Log Event column is noise, not
       detail. "Portal Login" rows carry source = "Portal Login", which is
       the highest-volume user log type, so this matters more than it looks. */
    const raw = detailFallbackKey ? r[detailFallbackKey] : '';
    const fallback = raw && String(raw).trim().toLowerCase() === type.trim().toLowerCase()
      ? '' : raw;
    return {
      ...r,
      _type: type,
      _fallback: fallback,
      _detail: logInfoText(r.log_info, fallback),
      _actor: r.display_name || '',
    };
  }), [logData, typeKey, detailFallbackKey]);

  const visible = useMemo(() => (
    showInternal ? rows : rows.filter((r) => r.is_customer_visible !== 0)
  ), [rows, showInternal]);

  const filterValues = useCallback((key) => {
    const vals = new Set();
    visible.forEach((r) => {
      const v = key === 'log_type' ? r._type : key === 'detail' ? r._detail : r._actor;
      vals.add(v ? String(v) : '(empty)');
    });
    return [...vals].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [visible]);

  const filtered = useMemo(() => {
    let out = visible;

    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        r._type.toLowerCase().includes(q) ||
        r._detail.toLowerCase().includes(q) ||
        r._actor.toLowerCase().includes(q) ||
        (r.create_time || '').toLowerCase().includes(q));
    }

    Object.entries(filters).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      out = out.filter((r) => {
        const v = key === 'log_type' ? r._type : key === 'detail' ? r._detail : r._actor;
        return values.includes(v ? String(v) : '(empty)');
      });
    });

    const pick = (r) => (
      sortCol === 'log_type' ? r._type
        : sortCol === 'detail' ? r._detail
          : sortCol === 'display_name' ? r._actor
            : r.create_time || '');
    return [...out].sort((a, b) => {
      const res = String(pick(a)).localeCompare(String(pick(b)), undefined,
        { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? res : -res;
    });
  }, [visible, search, filters, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const handleSort = useCallback((col) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return col;
    });
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((key, values) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
    setPage(1);
  }, []);

  const handleRemoveFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: (prev[key] || []).filter((v) => v !== value) }));
    setPage(1);
  }, []);

  const handleClearAll = useCallback(() => { setFilters({}); setPage(1); }, []);

  const SortIcon = ({ col }) => (
    sortCol !== col
      ? <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>
      : <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>
  );

  const Th = ({ col, filterable = true, wide = false }) => (
    <th className="dp-th" aria-sort={ariaSort(sortCol, sortDir, col)}>
      <div className="dp-th__content">
        <button className="dp-th__sort-btn" onClick={() => handleSort(col)}>
          {COLUMN_LABELS[col]} <SortIcon col={col} />
        </button>
        {filterable ? (
          <FilterPopover
            columnKey={col}
            label={COLUMN_LABELS[col]}
            values={filterValues(col)}
            selectedValues={filters[col] || []}
            onChange={handleFilterChange}
            wide={wide}
          />
        ) : null}
      </div>
    </th>
  );

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">{title}</h3>
        <div className="pp-section__actions">
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
            {filtered.length} entries
          </span>
        </div>
      </div>
      <div className="pp-section__body">
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                className="dp-search__input"
                type="text"
                placeholder="Search logs…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>

        <ActiveFilterBar
          filters={filters}
          columnLabels={COLUMN_LABELS}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAll}
        />

        {visible.length === 0 ? (
          <div className="dp-empty" style={{ marginTop: 'var(--space-4)' }}>
            <p className="dp-empty__title">{emptyTitle}</p>
            <p className="dp-empty__text">{emptyText}</p>
          </div>
        ) : (
          <>
            <div className="dp-table-wrap" style={{ marginTop: 'var(--space-3)' }}>
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    {/* Timestamps are near-unique per row, so a value checklist
                        would be one entry per row. Sort only. */}
                    <Th col="create_time" filterable={false} />
                    <Th col="log_type" />
                    <Th col="detail" wide />
                    {showActor ? <Th col="display_name" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, i) => (
                    /* The three ADT tables key on log_id; core_user_logs calls
                       its primary key user_log_id. Falling through to the index
                       keeps a table with neither from collapsing into one
                       duplicated React key. */
                    <tr key={row.log_id ?? row.user_log_id ?? row.palcare_device_log_id ?? i} className="dp-tr">
                      <td className="dp-td">
                        <span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                          {row.create_time ? convertIsoUTCToLocal(row.create_time, Number(tzOffset) || 0) : '—'}
                        </span>
                      </td>
                      <td className="dp-td">
                        <span className="dp-cell-text">{row._type || '—'}</span>
                      </td>
                      <td className="dp-td">
                        <LogDetailCell info={row.log_info} fallback={row._fallback} />
                      </td>
                      {showActor ? (
                        <td className="dp-td">
                          {/* Linked only with both an id and a name. An id with no name
                              would make a link out of an em dash, and a name with no id
                              has nowhere to go — either way, plain text is honest. */}
                          {linkUsers && row.modified_by && row.display_name ? (
                            <Link
                              to={`/profiles/user/${row.modified_by}`}
                              className="dp-cell-text dp-cell-text--link"
                              style={{ textDecoration: 'none' }}
                            >
                              {row.display_name}
                            </Link>
                          ) : (
                            <span className="dp-cell-text">{row.display_name || '—'}</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filtered.length > 0 ? (
              <Pagination
                page={safePage}
                totalPages={totalPages}
                total={filtered.length}
                perPage={perPage}
                onPageChange={setPage}
                onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                entityLabel={filtered.length === 1 ? 'log' : 'logs'}
                selectId="profile-logs-per-page"
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileLogsTable;
